import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import SectionHeader from '@/components/common/SectionHeader';
import { Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import apiService from '@/services/api';
import { Helmet } from 'react-helmet-async';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    const fetchFAQData = async () => {
      try {
        console.log('Fetching FAQ data...');
        const [faqResponse, categoryResponse] = await Promise.all([
          apiService.get('/public/faqs'),
          apiService.get('/public/faq/categories'),
        ]);

        console.log('FAQ Response:', faqResponse);
        console.log('Category Response:', categoryResponse);

        // FAQs are returned as an object grouped by category {general: [...], pricing: [...]}
        if (faqResponse?.success && faqResponse.data) {
          // Convert grouped object to flat array with proper category field
          let flatFaqs: any[] = [];
          const faqData = faqResponse.data;
          
          if (typeof faqData === 'object' && !Array.isArray(faqData)) {
            // It's a grouped object, flatten it
            for (const [category, faqItems] of Object.entries(faqData)) {
              if (Array.isArray(faqItems)) {
                faqItems.forEach((faq: any) => {
                  flatFaqs.push({
                    ...faq,
                    category: faq.category || category // Use category from FAQ or from key
                  });
                });
              }
            }
          } else if (Array.isArray(faqData)) {
            // It's already an array
            flatFaqs = faqData;
          }
          
          console.log('Processed FAQs:', flatFaqs);
          setFaqs(flatFaqs);
        }

        if (categoryResponse?.data && Array.isArray(categoryResponse.data)) {
          console.log('Setting categories:', categoryResponse.data);
          setCategories(categoryResponse.data);
          // Set default category
          if (categoryResponse.data.length > 0) {
            setSelectedCategory(categoryResponse.data[0].category);
          }
        }
      } catch (err) {
        console.error('Failed to fetch FAQ data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQData();
  }, []);

  const filteredFaqs = selectedCategory
    ? faqs.filter(faq => faq.category === selectedCategory)
    : faqs;

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>FAQs | Virewire - Your Managed Service Platform</title>
        <meta name="description" content="Answers to everything about our managed property services—How It Works, pricing, quality guarantees, and more." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground section-padding mt-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-6">Your Questions Answered</h1>
            <p className="text-xl text-primary-foreground/90 mb-2">
              Everything you need to know about how Virewire manages your property services.
            </p>
            <p className="text-lg text-primary-foreground/80">
              From booking to completion—we've got you covered.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          {/* Category Tabs */}
          {categories.length > 0 && (
            <div className="mb-12 flex flex-wrap gap-3 justify-center">
              {categories.map((cat) => (
                <button
                  key={cat.category}
                  onClick={() => setSelectedCategory(cat.category)}
                  className={`px-6 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === cat.category
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground border border-border hover:border-primary'
                  }`}
                >
                  {cat.category.charAt(0).toUpperCase() + cat.category.slice(1)} ({cat.count})
                </button>
              ))}
            </div>
          )}

          {/* FAQs List */}
          <div className="space-y-4">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-card border border-border rounded-lg overflow-hidden transition-all"
                >
                  <button
                    onClick={() =>
                      setExpandedId(expandedId === faq.id ? null : faq.id)
                    }
                    className="w-full p-6 flex items-center justify-between hover:bg-muted/50 transition-colors text-left"
                  >
                    <h3 className="font-semibold text-lg text-foreground pr-4">
                      {faq.question}
                    </h3>
                    {expandedId === faq.id ? (
                      <ChevronUp className="h-5 w-5 text-primary shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                  </button>

                  {expandedId === faq.id && (
                    <div className="px-6 pb-6 bg-muted/30 border-t border-border">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">
                  No FAQs available for this category yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-muted">
        <div className="container-custom max-w-2xl text-center">
          <h2 className="text-2xl font-bold mb-4">Didn't find your answer?</h2>
          <p className="text-muted-foreground mb-8">
            Get in touch with our team and we'll be happy to help you.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default FAQPage;
